import type { CardFilter } from './flashcards'
import { type Failure, RequestFailed, malformed, readJson, request } from './request'

/**
 * The examinations, as the service describes them.
 *
 * Three things live here and they are deliberately separate:
 *
 * - a **marking scheme** belongs to a subject and says how one *kind* of task is measured — what
 *   matters in it, which sources count, what the scale is. It is the text an assistant marks by.
 * - a **test** is a paper: one or more tasks, each with a question and a marking scheme of its own.
 *   A test may be filed under a subject; that filing orders the scheme picker and decides nothing.
 * - a **sitting** is one attempt at a paper, with the answers written in it, and at most one
 *   judgement. There can be any number of sittings of one test.
 *
 * **The judgement is never written from here.** It comes from an assistant through MCP, which is the
 * whole point of the feature; this side reads it and draws it.
 */

export interface MarkingScheme {
  readonly id: string
  readonly subjectId: string
  /** What that subject is called, so the picker can group without a second request. */
  readonly subjectName: string
  readonly name: string
  /** What matters, which sources count, what the scale is. */
  readonly description: string
  /** The markup the description is written in, as a media type. */
  readonly descriptionMarkup: string
  /** Whether a task is measured by it — which is why deleting it can be refused, with a reason. */
  readonly inUse: boolean
  readonly createdAt: string
  readonly updatedAt: string
}

export interface Exam {
  readonly id: string
  readonly title: string
  readonly subjectId: string | null
  readonly subjectName: string | null
  readonly tasks: readonly ExamTask[]
  /** How many sittings there are, so the overview can say it without fetching them. */
  readonly attempts: number
  readonly createdAt: string
  readonly updatedAt: string
}

export interface ExamTask {
  readonly id: string
  readonly position: number
  readonly schemeId: string
  readonly schemeName: string
  readonly prompt: Prompt
}

/**
 * What a task asks.
 *
 * One shape with a {@link kind} rather than three: `document` fills {@link documentId} and
 * {@link pages}, `written` fills {@link text} and {@link markup}, `cardset` fills
 * {@link cardSetMode}, {@link cardFilter} or {@link cardIds}, and {@link cards} — the cards
 * themselves, resolved fresh on every read. A `documentTitle` of `null` means the document is in the
 * trash or gone — the task stands, and the screen says the pages cannot be shown at the moment. A
 * card of {@link cards} with `gone: true` is the same idea, for a card that was removed.
 */
export interface Prompt {
  readonly kind: 'document' | 'written' | 'cardset'
  readonly documentId?: string | null
  readonly documentTitle?: string | null
  readonly documentPages?: number | null
  readonly pages?: readonly PageRange[]
  readonly text?: string | null
  readonly markup?: string | null
  readonly cardSetMode?: 'fixed' | 'dynamic' | null
  readonly cardIds?: readonly string[]
  readonly cardFilter?: CardFilter | null
  readonly cards?: readonly ExamCard[]
}

/** One card of a card-set task, resolved: enough to show the question and mark an answer. */
export interface ExamCard {
  readonly id: string
  readonly front: string
  readonly frontFormat: string
  readonly back: string
  readonly backFormat: string
  /** True when the card has been removed since the set was made. Its id and any answer stand. */
  readonly gone: boolean
}

/** A run of pages, one-based and inclusive at both ends. A single page has both ends equal. */
export interface PageRange {
  readonly from: number
  readonly to: number
}

export interface Attempt {
  readonly id: string
  readonly examId: string
  readonly startedAt: string
  /** When it was handed in, or `null` while it is still being written. Nothing is locked by it. */
  readonly handedInAt: string | null
  /**
   * How long somebody has been sitting at this test, in milliseconds **as of this answer**.
   *
   * The service works it out and this side counts on from it while {@link running} is true, rather
   * than adding up moments itself: the two clocks are not the same clock, and a machine whose time is
   * ten minutes out would otherwise show the wrong length rather than a late one.
   */
  readonly elapsedMs: number
  /** Whether the clock is running. False while the test is paused, and once it is handed in. */
  readonly running: boolean
  readonly updatedAt: string
  readonly answers: readonly Answer[]
  /** What was written for the cards of a card-set task — any number of entries per such task. */
  readonly cardAnswers: readonly CardAnswer[]
  readonly assessment: Assessment | null
}

export interface Answer {
  readonly taskId: string
  readonly text: string
  readonly markup: string
  readonly updatedAt: string
}

export interface CardAnswer {
  readonly taskId: string
  readonly cardId: string
  readonly text: string
  /** The markup this answer is written in, as a media type. */
  readonly markup: string
  readonly updatedAt: string
}

export interface Assessment {
  /** The mark, as it was given: `14`, `B+`, `4 von 6`. */
  readonly grade: string
  /** The scale it stands on. Shown beside it, always — a mark without its scale cannot be read. */
  readonly scale: string
  readonly signal: Signal
  readonly summary: string
  readonly remarks: readonly Remark[]
  readonly assessedAt: string
}

export type Signal = 'RED' | 'ORANGE' | 'YELLOW' | 'GREEN'

export interface Remark {
  readonly tone: 'GOOD' | 'BAD' | 'NEUTRAL'
  readonly text: string
  /**
   * The task whose answer this point is about, or `null` when it is about the sitting as a whole.
   *
   * Always together with {@link quote}: the two are one anchor, and the service refuses either alone.
   */
  readonly taskId: string | null
  /** The passage this point is about, word for word out of that answer, or `null`. */
  readonly quote: string | null
}

/** What a document says about itself while somebody is making a task out of it. */
export interface DocumentPages {
  readonly id: string
  readonly title: string
  /** How many pages it has, or `null` when this kind of file has none to draw. */
  readonly pages: number | null
}

export interface TaskDraft {
  /** The task this is, or left out for a new one. A draft that carries it keeps its answers. */
  readonly id?: string | null
  readonly schemeId: string
  readonly kind: 'document' | 'written' | 'cardset'
  readonly documentId?: string | null
  readonly pages?: readonly PageRange[]
  readonly text?: string | null
  readonly markup?: string | null
  readonly cardSetMode?: 'fixed' | 'dynamic' | null
  readonly cardIds?: readonly string[]
  readonly cardQuery?: string
  readonly cardTags?: readonly string[]
  readonly cardTagMode?: 'all' | 'any'
}

const SCHEMES = '/api/marking-schemes'
const EXAMS = '/api/exams'
const ATTEMPTS = '/api/attempts'

export async function fetchMarkingSchemes(signal?: AbortSignal): Promise<MarkingScheme[]> {
  return (await list(SCHEMES, signal)) as MarkingScheme[]
}

export async function fetchSchemesOfSubject(
  subjectId: string,
  signal?: AbortSignal,
): Promise<MarkingScheme[]> {
  const address = `/api/subjects/${encodeURIComponent(subjectId)}/marking-schemes`
  return (await list(address, signal)) as MarkingScheme[]
}

export async function addMarkingScheme(
  subjectId: string,
  name: string,
  description: string,
  descriptionMarkup: string,
): Promise<MarkingScheme> {
  return (await change(SCHEMES, 'POST', {
    subjectId,
    name,
    description,
    descriptionMarkup,
  })) as MarkingScheme
}

export async function changeMarkingScheme(
  id: string,
  name: string,
  description: string,
  descriptionMarkup: string,
): Promise<MarkingScheme> {
  return (await change(`${SCHEMES}/${encodeURIComponent(id)}`, 'PUT', {
    name,
    description,
    descriptionMarkup,
  })) as MarkingScheme
}

/** Refused while a task is still measured by it. The sentence says what to do about that. */
export async function removeMarkingScheme(id: string): Promise<void> {
  await change(`${SCHEMES}/${encodeURIComponent(id)}`, 'DELETE')
}

export async function fetchExams(signal?: AbortSignal): Promise<Exam[]> {
  return (await list(EXAMS, signal)) as Exam[]
}

export async function fetchExam(id: string, signal?: AbortSignal): Promise<Exam> {
  const response = await request(`${EXAMS}/${encodeURIComponent(id)}`, { signal })
  return readJson<Exam>(response)
}

export async function addExam(
  title: string,
  subjectId: string | null,
  tasks: readonly TaskDraft[],
): Promise<Exam> {
  return (await change(EXAMS, 'POST', { title, subjectId, tasks })) as Exam
}

/**
 * The test as a whole: title, filing and tasks in one request.
 *
 * One request and not three, because that is how it is edited — the order of the tasks, a new task
 * and a changed title are one act on one screen.
 */
export async function saveExam(
  id: string,
  title: string,
  subjectId: string | null,
  tasks: readonly TaskDraft[],
): Promise<Exam> {
  return (await change(`${EXAMS}/${encodeURIComponent(id)}`, 'PUT', {
    title,
    subjectId,
    tasks,
  })) as Exam
}

export async function removeExam(id: string): Promise<void> {
  await change(`${EXAMS}/${encodeURIComponent(id)}`, 'DELETE')
}

export async function fetchAttempts(examId: string, signal?: AbortSignal): Promise<Attempt[]> {
  return (await list(`${EXAMS}/${encodeURIComponent(examId)}/attempts`, signal)) as Attempt[]
}

export async function fetchAttempt(id: string, signal?: AbortSignal): Promise<Attempt> {
  const response = await request(`${ATTEMPTS}/${encodeURIComponent(id)}`, { signal })
  return readJson<Attempt>(response)
}

/** Sits the test: a new, empty sitting. Any number of them. */
export async function startAttempt(examId: string): Promise<Attempt> {
  return (await change(`${EXAMS}/${encodeURIComponent(examId)}/attempts`, 'POST')) as Attempt
}

/** Writes what was answered for one task. Called while somebody types, like everything written. */
export async function writeAnswer(
  attemptId: string,
  taskId: string,
  text: string,
  markup: string,
): Promise<Attempt> {
  const address = `${ATTEMPTS}/${encodeURIComponent(attemptId)}/answers/${encodeURIComponent(taskId)}`
  return (await change(address, 'PUT', { text, markup })) as Attempt
}

/** Writes what was answered for one card of a card-set task. Called while somebody types. */
export async function writeCardAnswer(
  attemptId: string,
  taskId: string,
  cardId: string,
  text: string,
  markup: string,
): Promise<Attempt> {
  const address = `${ATTEMPTS}/${encodeURIComponent(attemptId)}/card-answers/${encodeURIComponent(taskId)}/${encodeURIComponent(cardId)}`
  return (await change(address, 'PUT', { text, markup })) as Attempt
}

/**
 * Stops the clock and closes the test up, or opens it again and counts on.
 *
 * The closing up is the point and not a side effect: a pause that left the paper open would be a
 * clock somebody stops and then goes on writing under.
 */
export async function pauseAttempt(attemptId: string, paused: boolean): Promise<Attempt> {
  const address = `${ATTEMPTS}/${encodeURIComponent(attemptId)}/paused`
  return (await change(address, paused ? 'POST' : 'DELETE')) as Attempt
}

/** Says "this is what I wrote", or takes that back. Nothing is locked either way. */
export async function handIn(attemptId: string, handedIn: boolean): Promise<Attempt> {
  const address = `${ATTEMPTS}/${encodeURIComponent(attemptId)}/handed-in`
  return (await change(address, handedIn ? 'POST' : 'DELETE')) as Attempt
}

export async function removeAttempt(id: string): Promise<void> {
  await change(`${ATTEMPTS}/${encodeURIComponent(id)}`, 'DELETE')
}

/** What a document says about itself: its title, and how many pages there are to choose from. */
export async function fetchDocumentPages(
  documentId: string,
  signal?: AbortSignal,
): Promise<DocumentPages> {
  const response = await request(`${EXAMS}/documents/${encodeURIComponent(documentId)}`, { signal })
  return readJson<DocumentPages>(response)
}

/**
 * Where one page of a document is drawn.
 *
 * A picture and not the PDF: a task shows exactly the pages it names, and an embedded PDF at a page
 * anchor shows every other page around it — on an iPad it does not scroll at all.
 */
export function pageUrl(documentId: string, page: number): string {
  return `${EXAMS}/documents/${encodeURIComponent(documentId)}/pages/${String(page)}`
}

/** The pages a range names, in order. A single page is a range whose ends are equal. */
export function pagesIn(ranges: readonly PageRange[]): number[] {
  const pages: number[] = []
  for (const range of ranges) {
    for (let page = range.from; page <= range.to; page += 1) {
      pages.push(page)
    }
  }
  return pages
}

/**
 * Page ranges as somebody writes them: `3-5, 12`.
 *
 * The same spelling in both directions, so what is shown can be typed back in — which is what makes
 * the field a field rather than a control with a hidden format.
 */
export function rangesAsText(ranges: readonly PageRange[]): string {
  return ranges
    .map((range) =>
      range.from === range.to ? String(range.from) : `${String(range.from)}-${String(range.to)}`,
    )
    .join(', ')
}

/**
 * What somebody typed, read as page ranges.
 *
 * Forgiving about spaces and about the dash, strict about what a page is: anything that is not a
 * positive whole number is left out rather than guessed at, and a range that runs backwards is turned
 * round. The service normalises the result again — overlapping runs are joined there — so this only
 * has to be honest, not canonical.
 */
export function rangesIn(text: string): PageRange[] {
  const ranges: PageRange[] = []
  for (const part of text.split(',')) {
    const piece = part.trim().replace(/[–—]/g, '-')
    if (piece === '') {
      continue
    }
    const [first, second] = piece.split('-').map((end) => Number.parseInt(end.trim(), 10))
    if (first === undefined || Number.isNaN(first) || first < 1) {
      continue
    }
    const to = second === undefined || Number.isNaN(second) ? first : second
    ranges.push(to < first ? { from: to, to: first } : { from: first, to })
  }
  return ranges
}

/**
 * A length of time as somebody reads it off a clock: `7:14`, `1:03:22`.
 *
 * Hours only when there are any. A test is usually minutes, and a leading `0:` on every one of them
 * would be two characters saying nothing.
 */
export function asClock(ms: number): string {
  const whole = Math.max(0, Math.floor(ms / 1000))
  const seconds = whole % 60
  const minutes = Math.floor(whole / 60) % 60
  const hours = Math.floor(whole / 3600)
  const padded = (value: number) => String(value).padStart(2, '0')
  return hours === 0
    ? `${String(minutes)}:${padded(seconds)}`
    : `${String(hours)}:${padded(minutes)}:${padded(seconds)}`
}

/** What a traffic light says in words. Never the colour alone — see the design guidelines. */
export function signalInWords(signal: Signal): string {
  switch (signal) {
    case 'GREEN':
      return 'Good'
    case 'YELLOW':
      return 'Nearly there'
    case 'ORANGE':
      return 'Shaky'
    case 'RED':
      return 'Not yet'
  }
}

async function list(address: string, signal?: AbortSignal): Promise<unknown[]> {
  const response = await request(address, { signal })
  const payload: unknown = await response.json().catch(() => null)
  if (!Array.isArray(payload)) {
    throw malformed('The answer did not describe a list.')
  }
  return payload
}

/** The statuses that carry a code this side knows how to say out loud. */
const REFUSALS = [400, 404, 409, 413] as const

const SENTENCES: Readonly<Record<string, string>> = {
  'no-such-exam': 'That test is not there any more.',
  'no-such-attempt': 'That sitting is not there any more.',
  'no-such-marking-scheme': 'That marking scheme is not there any more.',
  'exam-without-tasks': 'A test needs at least one task.',
  'too-many-tasks': 'That is more tasks than one test holds.',
  'task-not-in-this-exam': 'That task belongs to another test.',
  'marking-scheme-in-use':
    'A task is still measured by this scheme. Change those tasks first, then it can go.',
  'marking-scheme-name-taken': 'This subject already has a scheme under that name.',
  'no-pages-chosen': 'Name at least one page.',
  'impossible-page-range': 'Those pages cannot exist.',
  'unknown-markup': 'MyLib does not write that markup.',
  'text-refused': 'That text cannot be kept as it stands.',
  'no-cards-chosen': 'Name at least one card.',
  'too-many-cards': 'That is more cards than one set holds.',
  'task-not-a-card-set': 'That task is not a card set.',
}

async function change(address: string, method: string, body?: unknown): Promise<unknown> {
  const response = await request(address, {
    method,
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    expect: [...REFUSALS],
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  })

  if (!response.ok) {
    throw refusalIn(await response.text(), response.status)
  }

  return response.status === 204 ? null : readJson<unknown>(response)
}

function parse(body: string): { code?: string; message?: string } | null {
  try {
    return JSON.parse(body) as { code?: string; message?: string }
  } catch {
    return null
  }
}

function refusalIn(body: string, status: number): RequestFailed {
  const problem = parse(body)
  const known = problem?.code === undefined ? undefined : SENTENCES[problem.code]

  const failure: Failure = {
    kind: status >= 500 ? 'service' : 'refused',
    message: known ?? problem?.message ?? 'MyLib could not do that.',
    detail: known === undefined ? null : (problem?.message ?? null),
    retryable: status >= 500,
    code: problem?.code ?? null,
  }
  return new RequestFailed(failure)
}
